/**
 * Does the worker have an F&O employment spell covering today?
 *
 * The Lifecycle page decides which actions to offer — Terminate / Extend
 * for an active worker, Reinstate for a terminated one — and it used to
 * decide that from SAGE alone (``EmployeeStatusCode`` T/D). Sage is the
 * wrong witness for one common case: a fixed-term contract that simply
 * EXPIRES. Nobody arms a Sage termination for that, so Sage keeps the
 * worker on ``A`` with a ``TerminationDate`` in the past, while F&O has
 * no spell covering today and shows EXITED.
 *
 * One worker (2026-09-02): contract ended 31 Aug, Sage
 * still ``A``. The page showed the active-worker tabs, the only door was
 * Extend, and the backend refused five times with "a terminated worker
 * is reinstated, not extended" — while the page offered no Reinstate tab
 * to act on that. Two people spent a morning on it.
 *
 * F&O is the authority on "is there a current spell". This mirrors the
 * backend's own preflight (``_get_spells_covering``): a spell covers a
 * day iff ``EmploymentStartDate <= day <= EmploymentEndDate``.
 *
 * Dates are compared as SOUTH AFRICAN calendar days. F&O stores UTC
 * instants and SAST midnight is the prior day at 22:00Z, so slicing the
 * raw value is a day early for every midnight-based date.
 */

import type { LiveEmployeeSnapshot } from './snapshot-util';

export type Spell = Record<string, unknown>;

/** Today's date in South Africa (UTC+2, no DST), as YYYY-MM-DD. */
export function sastToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** An F&O timestamp (or plain date) as its SAST calendar day. */
export function sastDay(value: unknown): string | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (!text.includes('T')) return text.slice(0, 10);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return sastToday(parsed);
}

/** Backend rule, verbatim: start <= day <= end (inclusive, SAST days). */
export function spellCoversDay(spell: Spell, dayIso: string): boolean {
  const start = sastDay(spell.EmploymentStartDate);
  const end = sastDay(spell.EmploymentEndDate);
  if (!start || !end) return false;
  return start <= dayIso && dayIso <= end;
}

/**
 * ``true`` = a spell covers today, ``false`` = none does,
 * ``null`` = the snapshot carries no spell data, so nothing can be said.
 *
 * ``null`` matters: the caller must NOT treat "we couldn't tell" as
 * "terminated". Routing an active worker to Reinstate on missing data
 * would be the mirror image of the bug this fixes.
 *
 * Prefers the full history (``EmploymentsV2_All``) so a pending FUTURE
 * spell is not mistaken for a current one; falls back to the single
 * ``EmploymentsV2`` row older snapshots carry.
 */
export function hasSpellCoveringToday(
  fo: {
    EmploymentsV2?: Spell;
    EmploymentsV2_All?: Spell[];
  } | null | undefined,
  now: Date = new Date(),
): boolean | null {
  if (!fo) return null;
  const today = sastToday(now);
  const all = Array.isArray(fo.EmploymentsV2_All) ? fo.EmploymentsV2_All : null;
  if (all && all.length > 0) {
    return all.some((s) => spellCoversDay(s, today));
  }
  const single = fo.EmploymentsV2;
  if (single && Object.keys(single).length > 0) {
    return spellCoversDay(single, today);
  }
  return null;
}

/**
 * Is a Sage termination still ahead: armed, not yet effected?
 *
 * ``terminationDate`` is a plain YYYY-MM-DD day. Sage sends it without a
 * time zone, so the caller slices it; never read it through ``sastDay``.
 * "Today" is the SOUTH AFRICAN day: the UTC day is still yesterday until
 * 02:00 SAST, which made a termination dated today look a day away.
 */
export function sageTerminationPending(
  terminationDate: string,
  now: Date = new Date(),
): boolean {
  return !!terminationDate && terminationDate > sastToday(now);
}

/**
 * Does the Lifecycle page route this worker to Reinstate?
 *
 * Termination state drives which panel to show. The authoritative
 * signal is ``EmployeeStatusCode``:
 *   A — Active steady-state
 *   N — New (recently-hired, pre-first-payslip)
 *   C — Casual
 *   T — Terminated (recent — still in the live Employee table)
 *   D — Deleted / Archived (Sage moved an old terminated record to
 *       the archive table; ``TerminationDate`` is still populated so
 *       the worker is functionally equivalent to T from the
 *       Reinstate workflow's POV).
 * We treat T and D the same way — both route to ReinstateEmployeePanel.
 * ``TerminationDate`` is intentionally NOT consulted: Sage leaves it
 * populated after a successful reinstate (status flips T→N/A but the
 * date column persists as historical debris), so it would
 * false-positive on already-active workers.
 */
export function lifecycleIsTerminated(
  snapshot: Partial<Pick<LiveEmployeeSnapshot, 'sage_record' | 'fo_snapshot'>> | null | undefined,
  now: Date = new Date(),
): boolean {
  const statusCode = (
    (snapshot?.sage_record?.employeeStatusCode as string | undefined) ??
    (snapshot?.sage_record?.EmployeeStatusCode as string | undefined) ??
    ""
  ).toUpperCase();
  // ⚠ Sage flips EmployeeStatusCode to T as soon as a fixed-term
  // termination is ARMED — not when it takes effect.  One worker read
  // T on 2026-07-29 with TerminationDate 2026-07-31 and a live F&O
  // spell still running to that date.  The worker had not left; they were two
  // days from the end of a contract she was still working.  Routing
  // that worker to Reinstate is wrong twice over: there is nothing to
  // restore, and it hides Extend contract — the one action the
  // situation actually calls for.
  //
  // Here — and ONLY here — TerminationDate is a safe signal.  The
  // caution above is about using a POPULATED date to infer termination
  // (Sage leaves it as debris after a reinstate).  A date in the
  // FUTURE is different: it cannot describe a departure that has
  // already happened, so it can only mean "armed, not yet effected".
  const terminationDateIso = (
    (snapshot?.sage_record?.terminationDate as string | undefined) ??
    (snapshot?.sage_record?.TerminationDate as string | undefined) ??
    ""
  ).slice(0, 10);
  // Compared with the South African day: the UTC day is still yesterday
  // until 02:00 SAST.
  const terminationPending = sageTerminationPending(terminationDateIso, now);
  // Sage is the wrong witness for a fixed-term contract that simply
  // EXPIRES (the 2 Sep 2026 case, see the top of this file). F&O is the authority
  // on "is there a current spell"; this mirrors the backend's own
  // preflight. A null (no spell data) deliberately does NOT flip the
  // worker to terminated: routing an active worker to Reinstate on
  // missing data would be the mirror image of the bug.
  const foSpellExpired =
    hasSpellCoveringToday(snapshot?.fo_snapshot, now) === false;
  return ((statusCode === "T" || statusCode === "D") && !terminationPending)
    || foSpellExpired;
}
